import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { OrgId, OrgResponse, PaymentId, PaymentMethodResponse, PaymentMethodType } from '@business/entities/organization';
import { CommonService, OrderService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useHistory } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Card, CardBody, CardTitle, DropdownMenu, DropdownToggle, Spinner, UncontrolledDropdown } from 'reactstrap';
import { TimeMaterialsListBloc } from './TimeMaterialsListBloc';
import { TMStageId, CompactTimeMaterialResponse, TimeMaterialStatus, TimeMaterialResponse, TimeMaterialId } from '@business/entities/tm';
import { openTimeMaterialCancelModal } from '@modules/timeMaterials/TimeMaterialOverlay/TimeMaterialCancelModal';
import { openTimeMaterialOverlay } from '@modules/timeMaterials/TimeMaterialOverlay';
import { openTimeMaterialPaymentModal } from '../TimeMaterialPaymentModal';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ProcessTmInvoiceFormData } from '../TimeMaterialPaymentModal/TimeMaterialPaymentModal';
import { R } from '@core/utils/r';
import { HasOrderName } from '@modules/common/HasOrderName';
import { useLoadingState } from '@core/utils/repository/loading_state';

function defineOrderPageForm() {
  return formGroup({
    status: formControl<TimeMaterialStatus>({
      initialValue: TimeMaterialStatus.open,
    }),
    stage: formControl<TMStageId>({
      placeholder: 'Select Stage',
    }),
  });
}
export type OrdersFilterData = FormDataType<typeof defineOrderPageForm>;

export interface TimeMaterialsListProps {
  orgId: OrgId;
  organization: OrgResponse;
  isModal?: boolean;
  methods: PaymentMethodResponse[];
  //   boardId: TMBoardId;
}

export const TimeMaterialsList = memo((props: TimeMaterialsListProps) => {
  const { orgId, methods } = props;
  const history = useHistory();
  const form = useMemo(() => defineOrderPageForm(), []);
  const formValue = useObservable(form.value$);
  const bloc = useBloc(TimeMaterialsListBloc, orgId, '1');
  const orders = useObservable(bloc.filteredOrders$, []) as TimeMaterialResponse[];
  const organization = useObservable(bloc.organization$, null);
  const statusItems = useMemo(() => CommonService.selectItems(OrderService.allStatuses(), i => OrderService.getStatusTitle(i)), []);
  const loading = useLoadingState(bloc.loading);

  const onProcessPayment = useCallback(
    (id: TimeMaterialId, paymentId: PaymentId, amount: number, type: PaymentMethodType, data: ProcessTmInvoiceFormData) => {
      return of(null).pipe(
        switchMap(() => {
          return bloc.processTmInvoicePayment(id, paymentId, amount, data);
        }),
      );
    },
    [bloc],
  );

  const onPayment = useCallback(
    (tm: CompactTimeMaterialResponse) => {
      openTimeMaterialPaymentModal({
        max: tm.balance,
        methods: props.methods,
        onSubmit: (paymentId, amount, type, data) => onProcessPayment(tm.id, paymentId, amount, type, data),
        currency: organization ? organization.currency : 'USD',
        tmId: tm.id,
      });
    },
    [onProcessPayment, organization, props.methods],
  );

  useEffect(() => {
    if (formValue) {
      bloc.filters$.next(formValue);
    }
  }, [bloc, formValue]);

  const columns: GCColumn<TimeMaterialResponse>[] = useMemo(
    () => [
      {
        id: 'name',
        name: `Name`,
        sortField: 'name',
        minWidth: '150px',
        sortable: true,
        cell: row => (
          <a className="ml-0" onClick={() => openTimeMaterialOverlay({ orderId: row.id, boardId: row.boardId })}>
            {!R.isEmpty(row.name) && <HasOrderName>{row.name} - </HasOrderName>}
            {row.orderNumber}
          </a>
        ),
      },
      {
        name: 'Board',
        sortField: 'board',
        cell: item => item.boardName,
        minWidth: '80px',
      },
      {
        name: 'Stage',
        sortField: 'stage',
        cell: item => item.stage?.name,
        minWidth: '80px',
      },
      {
        name: 'Total',
        sortField: 'total',
        cell: item => Formatter.formatCurrency(item.total, 'USD'),
        minWidth: '80px',
      },
      {
        name: 'Balance',
        sortField: 'balance',
        cell: item => Formatter.formatCurrency(item.balance, 'USD'),
        minWidth: '80px',
      },
      {
        name: 'Date',
        sortField: 'date',
        cell: item => Formatter.formatTime(item.created),
        minWidth: '80px',
      },
      {
        name: '',
        sortField: 'action',
        width: '80px',
        cell: order => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                <i className="bx bx-dots-horizontal-rounded"></i>
              </DropdownToggle>
              <DropdownMenu container="body" className="dd-menu_outside">
                <a className={`dropdown-item ${order.invoices.length > 0 ? '' : 'disabled'}`} onClick={() => onPayment(order)}>
                  Process Payment
                </a>

                {order.canEdit && (
                  <a
                    className={`dropdown-item`}
                    onClick={() => {
                      history.replace(`/order/edit/${order.organization.id}/${order.id}`);
                    }}
                  >
                    Edit
                  </a>
                )}

                {order.status !== TimeMaterialStatus.canceled && (
                  <a className="dropdown-item text-danger" onClick={() => openTimeMaterialCancelModal({ order: order as TimeMaterialResponse })}>
                    Cancel
                  </a>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [history, onPayment],
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          Work Orders
          <div className="d-flex">
            <div style={{ minWidth: 150 }}>
              <FormSelect withoutGroup control={form.controls.status} items={statusItems} />
            </div>
            <Button
              extraClassNames="ml-4"
              intent="primary"
              onClick={() => {
                history.push(`/time-materials/create/${orgId}?boardId=1`);
              }}
            >
              Add Work Order
            </Button>
          </div>
        </CardTitle>
        {loading.loading && <Spinner className="ml-2 font-size-12" size="sm" color="primary" />}
        {!loading.loading && <GCTable columns={columns} data={orders} noSearch subHeader={false} />}
      </CardBody>
    </Card>
  );
});
